import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ingredientService } from '../../services/ingredientService';
import { Ingredient, IngredientFamily, CreateIngredientRequest, UpdateIngredientRequest } from '../../types/ingredient';
import Button from '../UI/Button';

interface IngredientModalProps {
  ingredient?: Ingredient | null;
  ingredientFamilies: IngredientFamily[];
  onClose: () => void;
  onSuccess: () => void;
}

const IngredientModal: React.FC<IngredientModalProps> = ({
  ingredient,
  ingredientFamilies,
  onClose,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const isEditing = !!ingredient;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateIngredientRequest>({
    defaultValues: {
      code: ingredient?.code || '',
      description: ingredient?.name || '',
      ingredientFamilyId: ingredient?.ingredientFamilyId || '',
      status: ingredient?.status || 'ACTIVE',
    },
  });

  useEffect(() => {
    if (ingredient) {
      reset({
        code: ingredient.code || '',
        description: ingredient.name,
        ingredientFamilyId: ingredient.ingredientFamilyId,
        status: ingredient.status,
      });
    }
  }, [ingredient, reset]);

  const createMutation = useMutation({
    mutationFn: (data: CreateIngredientRequest) => ingredientService.createIngredient(data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (e: any) => {
      const apiMsg = e?.response?.data?.message;
      const zodMsg = e?.response?.data?.errors?.[0]?.message;
      setError(apiMsg || zodMsg || 'Failed to create ingredient');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: UpdateIngredientRequest) =>
      ingredientService.updateIngredient(ingredient!.id, data),
    onSuccess: () => {
      onSuccess();
    },
    onError: (e: any) => {
      const apiMsg = e?.response?.data?.message;
      const zodMsg = e?.response?.data?.errors?.[0]?.message;
      setError(apiMsg || zodMsg || 'Failed to update ingredient');
    },
  });

  const onSubmit = (data: CreateIngredientRequest) => {
    setError(null);
    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Transition appear show={true} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                    {isEditing ? 'Edit Ingredient' : 'Create New Ingredient'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                {error && (
                  <div className="mb-4 rounded-lg bg-red-50 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  <div>
                    <label htmlFor="description" className="form-label">
                      Description *
                    </label>
                    <input
                      {...register('description', {
                        required: 'Description is required',
                        maxLength: {
                          value: 255,
                          message: 'Description must be less than 255 characters',
                        },
                      })}
                      type="text"
                      className="form-input"
                      placeholder="Enter ingredient description"
                    />
                    {errors.description && (
                      <p className="form-error">{errors.description.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="code" className="form-label">
                      Code
                    </label>
                    <input
                      {...register('code', {
                        maxLength: {
                          value: 50,
                          message: 'Code must be less than 50 characters',
                        },
                      })}
                      type="text"
                      className="form-input"
                      placeholder="Enter ingredient code (optional)"
                    />
                    {errors.code && (
                      <p className="form-error">{errors.code.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="ingredientFamilyId" className="form-label">
                      Ingredient Family *
                    </label>
                    <select
                      {...register('ingredientFamilyId', {
                        required: 'Ingredient family is required',
                      })}
                      className="form-input"
                    >
                      <option value="">Select ingredient family</option>
                      {ingredientFamilies.map((family) => (
                        <option key={family.id} value={family.id}>
                          {family.name}
                        </option>
                      ))}
                    </select>
                    {errors.ingredientFamilyId && (
                      <p className="form-error">{errors.ingredientFamilyId.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="status" className="form-label">
                      Status
                    </label>
                    <select
                      {...register('status')}
                      className="form-input"
                    >
                      <option value="ACTIVE">Active</option>
                      <option value="INACTIVE">Inactive</option>
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onClose}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      loading={isLoading}
                    >
                      {isEditing ? 'Update' : 'Create'}
                    </Button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default IngredientModal;
